import { EVENT } from "@/constants/event";
import {
  AUTH_RIGHT_PACKAGE_CODES,
  isBigCompanyByUser,
  isCompanyVipByUser,
  isExperienceVipByUser,
  PERSONAL_NO_AUTH,
} from "@/constants/vip";
import useAppStore from "@/context/store";
import { getTypeNum, handleDownloadFileName } from "@/utils/download";
import getTimeDuration from "@/utils/getTimeDuration";
import imageUrl from "@/utils/imageUrl";
import useReq from "@/utils/useReq";
import { Button, Select } from "antd";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { Else, If, Then, When } from "react-if";
import { getDownloadInfo, getPreDownloadInfo } from "../../services";
import { REQUEST_MESSAGE_KEY } from "../../utils/request";
import HBIcon from "../HBIcon";
import HBMessage from "../HBMessage";
import HBModal, { ModalProps } from "../HBModal";
import ModalWrap from "../HBModal/ModalWrap";
import Image from "../Image";
import { AdditionGoodDialog, BuyVipDialog } from "../Vip";
import styles from "./index.module.less";

const getFormatTip = (format) => {
  switch (format) {
    case "PSD":
      return "（建议使用Adobe Photoshop软件打开）";
    case "C4D":
      return "（建议使用CINEMA 4D软件打开）";
    case "EPS":
    case "AI":
      return "（建议使用Adobe Illustrator软件打开）";
    case "CDR":
      return "（建议使用CorelDRAWru软件打开）";
    case "ZIP":
    case "RAR":
      return "（建议您先解压，再根据素材格式用相应软件打开）";
    default:
      return "";
  }
};

interface MaterialDownloadInfoModalProps extends ModalProps {
  pin: IPin;
  info: PreDownLoadRes;
  file: {
    file_format: string;
    file_size: string;
    file_type: string;
  };
  userRights: IUserRight[];
  subjects: CERTSubject[];
  defaultSelectSubject: number;
  onDownload?: () => void;
  onOk?: () => void;
}

function MaterialDownloadInfoModal({
  pin,
  info,
  file,
  userRights,
  subjects,
  defaultSelectSubject,
  onDownload,
  onOk,
  ...props
}: MaterialDownloadInfoModalProps) {
  const materialInfo = pin.material_info;
  const format = file?.file_format?.toUpperCase() || "";
  const user = useAppStore((s) => s.user);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [preDownloadInfo, setPreDownloadInfo] = useState<PreDownLoadRes>(info);

  const { runAsync: runDownload, loading } = useReq(getDownloadInfo, {
    manual: true,
  });

  const subjectSelectOption = useMemo(() => {
    if (!subjects) {
      return [];
    }

    return subjects
      .filter((item) => item.subject_info?.name)
      .map((item) => ({
        label: item.subject_info.name,
        value: item.id,
      }));
  }, [subjects]);

  const [subjectValue, setSubjectValue] = useState(defaultSelectSubject);

  const { run: preDownload } = useReq(
    async (vip_cert_subject_id?: number) => {
      const result = await getPreDownloadInfo({
        material_id: pin.material_info?.id,
        download_type: 1,
        type: getTypeNum(pin?.file_material?.material_category),
        ...(vip_cert_subject_id ? { vip_cert_subject_id } : null),
      });

      if (result.err) {
        HBMessage.error("当前授权主体无法下载");
        return;
      }

      setPreDownloadInfo(result);
    },
    { manual: true }
  );

  const rightData = preDownloadInfo?.rights[0];

  const onDownloadClick = useCallback(async () => {
    try {
      const materialInfo = pin.material_info;
      const materialId = materialInfo.id;

      if (loading) {
        return;
      }

      let format = "";

      if (pin?.file_material?.material_category === "视频") {
        format = "HD";
      } else if (pin?.file_material?.material_category === "高端免抠") {
        format = file.file_type;
      }

      setDownloadLoading(true);
      const result = await runDownload({
        material_id: materialId,
        type: getTypeNum(pin?.file_material?.material_category), // 素材类型
        download_type: 1, //会员身份下载
        system_code: preDownloadInfo?.vips[0].system_code,
        pin_id: pin?.pin_id,
        filename: handleDownloadFileName(pin, file),
        ...(file?.file_format ? { material_type: file.file_format } : null),
        ...(format ? { format } : null),
        // 授权书ID
        ...(subjectValue ? { vip_cert_subject_id: subjectValue } : null),
      });

      setDownloadLoading(false);

      if (result.code === 1) {
        window.dispatchEvent(
          // @ts-ignore
          new CustomEvent(EVENT.MATERIAL_DOWNLOAD, { detail: result, pin: pin })
        );

        onOk && onOk();
      } else {
        console.warn(result.message || result.msg);

        let errMsg = result.message || result.msg;
        if (errMsg === "视觉中国请求异常！") {
          errMsg = "素材异常，暂时无法下载";
        }

        HBMessage.error({
          content: errMsg,
          key: REQUEST_MESSAGE_KEY,
          duration: 3,
        });
      }
    } catch (error) {
      setDownloadLoading(false);
      throw error;
    }
  }, [
    pin,
    loading,
    runDownload,
    preDownloadInfo?.vips,
    subjectValue,
    file,
    onOk,
  ]);

  const onIncreate = useCallback(() => {
    // @ts-ignore
    props.onCancel && props.onCancel();

    AdditionGoodDialog.open({
      onOk: onDownload,
    });
  }, [onDownload, props]);

  const openBuyVipDialog = useCallback(() => {
    // @ts-ignore
    props.onCancel && props.onCancel();
    BuyVipDialog.open({});
  }, [props]);

  // 下载按钮是否禁用, 下载次数不足时禁用
  const disabled = useMemo(() => {
    return (
      // 授权书有效
      preDownloadInfo?.valid_cert_subject &&
      preDownloadInfo?.rights[0].inspect.is_finite_quantity &&
      preDownloadInfo?.rights[0].inspect.left <= 0 &&
      !preDownloadInfo.is_download
    );
  }, [preDownloadInfo]);

  // 是否是大企业会员
  const isBigCompany = useMemo(() => {
    return isBigCompanyByUser(user);
  }, [user]);

  // 无商用授权判断
  const noAuth = useMemo(() => {
    return userRights.find((item) => item.code === PERSONAL_NO_AUTH);
  }, [userRights]);

  const authText = useMemo(() => {
    if (noAuth) {
      return (
        <>
          仅供交流学习，<span className="color-primary">不可商用</span>
        </>
      );
    }

    const auth_rights = userRights.filter((item) =>
      AUTH_RIGHT_PACKAGE_CODES.includes(item.right_package_code)
    );

    if (isCompanyVipByUser(user)) {
      if (auth_rights.length) {
        return "企业高级授权";
      }
      return "企业标准授权";
    } else if (isBigCompanyByUser(user)) {
      return "大企业定制授权";
    } else {
      return "个人标准授权";
    }
  }, [noAuth, userRights, user]);

  // 会员剩余天数
  const vipLeftDays = useMemo(() => {
    const expireTime = user.extra.material_vip_expire_at - Date.now();

    const days = dayjs.duration(expireTime).asDays();
    return Math.floor(days);
  }, [user]);

  return (
    //@ts-ignore
    <HBModal
      className={styles.modal}
      title="下载花瓣素材"
      width={480}
      footer={null}
      {...props}
    >
      <div className={styles.container}>
        <div className={styles.info}>
          <Image src={imageUrl(pin.file)} alt="" width={50} height={50} />
          <div>
            <div className={styles.title} title={materialInfo?.title}>
              {materialInfo?.title}
            </div>
            <div>
              <span className={styles.red}>{format}</span>
              <span className={styles.gray}>{getFormatTip(format)}</span>
            </div>
          </div>
        </div>

        {subjects?.length > 1 && (
          <div className={styles.item}>
            <div className={styles.label}>授权主体</div>
            <div className={styles.value}>
              <Select
                className={styles.subjectSelect}
                suffixIcon={
                  <HBIcon
                    type="ic_arrow_drop_down"
                    className={styles.selectArrow}
                  />
                }
                value={subjectValue}
                options={subjectSelectOption}
                onChange={(value) => {
                  setSubjectValue(value);
                  preDownload(value);
                }}
                dropdownClassName={styles.subjectSelectDropdown}
              ></Select>

              <a href="/me/subject" target="_blank">
                管理
              </a>
            </div>
          </div>
        )}

        <div className={styles.item}>
          <div className={styles.label}>商用授权</div>
          <div className={styles.value}>{authText}</div>
        </div>
        <div className={styles.item}>
          <div className={styles.label}>下载次数</div>
          <div className={styles.value}>
            {preDownloadInfo.is_download ? (
              <>
                消耗 <span className={styles.red}>0</span> 次 (
                {noAuth ? "下载过" : "授权过"}的素材，会员期内可以免费重复下载)
              </>
            ) : (
              <>
                消耗 <span className={styles.red}>1</span> 次{" "}
                {isBigCompany
                  ? `(还可用${rightData.inspect.left}次)`
                  : `(今天还可用${rightData.inspect.left}次)`}
              </>
            )}
          </div>
        </div>
        <If condition={rightData.inspect.left === 0 && !isBigCompany}>
          <Then>
            <div className={styles.alert}>
              <HBIcon type="ic_notifications" />
              <span className={styles.text}>
                当前下载次数已用完，如需下载请增加次数
              </span>
              <a onClick={onIncreate}>
                去增加 <HBIcon type="ic_chevron_right" />
              </a>
            </div>
          </Then>
          <Else>
            <When condition={vipLeftDays < 7}>
              <div className={styles.alert}>
                <HBIcon type="ic_notifications" />
                <span className={styles.text}>
                  花瓣素材会员将在
                  {getTimeDuration(user.extra.material_vip_expire_at, "", true)}
                  后到期
                </span>
                <a onClick={openBuyVipDialog}>
                  去续费 <HBIcon type="ic_chevron_right" />
                </a>
              </div>
            </When>
          </Else>
        </If>
        <div className={styles.footer}>
          {isExperienceVipByUser(user) ? (
            <Button
              className={styles.button}
              onClick={openBuyVipDialog}
              data-gd-click="button_click"
              data-button-name="开通VIP"
            >
              开通VIP
            </Button>
          ) : (
            !isBigCompany && (
              <Button
                className={styles.button}
                onClick={onIncreate}
                data-gd-click="button_click"
                data-button-name="增加下载次数"
              >
                增加下载次数
              </Button>
            )
          )}
          <Button
            className={styles.button}
            type="primary"
            onClick={onDownloadClick}
            disabled={disabled}
            loading={downloadLoading}
          >
            {preDownloadInfo?.is_download ? "再次下载" : "确定下载"}
          </Button>
        </div>
      </div>
    </HBModal>
  );
}

const MaterialDownloadDialog = ModalWrap(MaterialDownloadInfoModal);

export default MaterialDownloadDialog;
